import React, {
  createContext,
  useContext,
  useState,
  // useEffect,
  useCallback,
} from "react";
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
} from "services/api";

const ServiceContext = createContext();

export const ServiceProvider = ({ children }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch services by ATV ID
  const fetchServicesByATV = useCallback(async (atvId) => {
    if (!atvId) return;

    setLoading(true);
    try {
      const response = await getRequest(`/service/atv/${atvId}`);
      if (response.success) {
        setServices(response.data || []);
        setError(null);
      } else {
        throw new Error(response.message || "Failed to fetch services");
      }
    } catch (err) {
      setError(err.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all services (for admin purposes)
  const fetchAllServices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRequest("/service");
      if (response.success) {
        setServices(response.data || []);
        setError(null);
      } else {
        throw new Error(response.message || "Failed to fetch services");
      }
    } catch (err) {
      setError(err.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new service
  const createService = useCallback(
    async (serviceData) => {
      setLoading(true);
      try {
        const response = await postRequest("/service", serviceData);
        if (response.success) {
          // Refresh services for the ATV
          if (serviceData.atv_id) {
            await fetchServicesByATV(serviceData.atv_id);
          }
          setError(null);
          return response.data;
        } else {
          throw new Error(response.message || "Failed to create service");
        }
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchServicesByATV]
  );

  // Update service
  const updateService = useCallback(
    async (id, serviceData) => {
      setLoading(true);
      try {
        const response = await patchRequest(`/service/${id}`, serviceData);
        if (response.success) {
          // Refresh services for the ATV
          if (serviceData.atv_id) {
            await fetchServicesByATV(serviceData.atv_id);
          }
          setError(null);
          return response.data;
        } else {
          throw new Error(response.message || "Failed to update service");
        }
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchServicesByATV]
  );

  // Delete service
  const deleteService = useCallback(
    async (id, atvId) => {
      setLoading(true);
      try {
        const response = await deleteRequest(`/service/${id}`);
        if (response.success) {
          // Refresh services for the ATV
          if (atvId) {
            await fetchServicesByATV(atvId);
          }
          setError(null);
        } else {
          throw new Error(response.message || "Failed to delete service");
        }
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchServicesByATV]
  );

  // Get service by ID
  const getServiceById = useCallback(
    (id) => {
      return services.find((service) => service.id === id);
    },
    [services]
  );

  const value = {
    services,
    loading,
    error,
    fetchServicesByATV,
    fetchAllServices,
    createService,
    updateService,
    deleteService,
    getServiceById,
  };

  return (
    <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>
  );
};

export const useServices = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error("useServices must be used within a ServiceProvider");
  }
  return context;
};
